import { STHYPE_ADDRESS, STHYPE_OVERSEER_ADDRESS } from '@hyperswap/addresses'
import { Currency } from '@uniswap/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import IntroCard from 'components/IntroCard'
import { RowBetween } from 'components/Row'
import { SkeletonText } from 'components/Skeleton'
import { API_GRAPHQL_STHYPE, LINKS } from 'constants/links'
import { useActiveWeb3React } from 'hooks'
import { useToken } from 'hooks/Tokens'
import React, { useContext, useState } from 'react'
import { useWalletModalToggle } from 'state/application/hooks'
import styled, { ThemeContext } from 'styled-components'
import { TYPE } from 'theme'
import Stake from './Stake'
import Unstake from './Unstake'
import { useTokenBalance } from 'state/wallet/hooks'
import { roundToDecimalPlace } from 'utils/data'
import { getContract } from 'utils'
import { abi as STHypeOverseerABI } from '../../../abis/sthype_overseer.json'
import { abi as STHypeABI } from '../../../abis/sthype.json'
import { formatUnits } from 'ethers/lib/utils'
import axios from 'axios'
export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 2px;
`

export const StyledTabButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  border: none;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: white;
  padding: 8px 16px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 14px;
  transition: all 300ms;
  background: ${({ isActive }) => (isActive ? '#4AFC38' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'black' : 'white')};

  &:hover {
    color: #4afc38;
  }

  ${({ isActive }) =>
    isActive &&
    `
    &:hover {
      color: black !important;
    }
  `}
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`
const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
    flex-direction: column-reverse;
  `};
`

const Links = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 6px;
  gap: 2px;
  a {
    padding: 2px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 300ms ease;

    &:hover {
      background: rgb(30, 30, 30);
      svg {
        color: white;
      }
    }
  }
  svg {
    width: 16px;
    height: 16px;
    color: black;
  }
`

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: calc(55% - 8px) calc(45% - 8px);
  }
`

const EntriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
  gap: 15px 0.5rem;
  padding: 0;
  margin-top: 6px;
`

const SubContainer = styled.div<{ bg1: boolean }>`
  background: ${({ theme }) => theme.bg0};
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`
const SubContainerHeader = styled.div`
  border-radius: 14px; 
   background: ${({ theme }) => theme.bg1}
  padding: 20px;
  p {
    color: ${({ theme }) => theme.text2}
    font-size: 12px
    margin-bottom: 0;
  }
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const EarnSTHYPE = () => {
  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const theme = useContext(ThemeContext)
  const [tab, setTab] = React.useState<0 | 1>(0)
  const stHYPEToken = useToken(STHYPE_ADDRESS)

  const [data, setData] = useState<any>({
    maxRedeemable: null,
    apr: null,
    totalHYPEStaked: null,
    uniqueMintsFrom: null,
    totalSTHYPE: null,
    rateStHYPEToHYPE: 1,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const stToken = useToken(STHYPE_ADDRESS)
  const tokenBalance = useTokenBalance(account ?? undefined, stToken ?? undefined)

  React.useEffect(() => {
    async function fetchData() {
      try {
        if (library && account) {
          // Fetch data from the overseer contract
          const overseerV1Contract = getContract(STHYPE_OVERSEER_ADDRESS, STHypeOverseerABI, library, account)

          const stHYPEContract = getContract(STHYPE_ADDRESS, STHypeABI, library, account)

          const assetsToShare = await stHYPEContract.balanceToShares(`${Math.pow(10, 18)}`)

          const maxRedeemable = await overseerV1Contract.maxRedeemable()

          // Axios POST request to the GraphQL endpoint
          const response = await axios.post(API_GRAPHQL_STHYPE, {
            query: `
            query MyQuery {
               statsAtRebases {
                  apr
                }
              
              totalCurrentlyStaked
              uniqueMintsFrom
            }
          `,
            variables: null,
            operationName: 'MyQuery',
          })

          // Extract the APR value from the GraphQL response
          const apr = response?.data?.data?.statsAtRebases?.apr
          const totalHYPEStaked = response?.data?.data?.totalCurrentlyStaked
          const uniqueMintsFrom = response?.data?.data?.uniqueMintsFrom

          const rateHYPEToStHYPE = formatUnits(assetsToShare, 18)

          const rateStHYPEToHYPE = 1 / parseFloat(rateHYPEToStHYPE ?? 1)

          // Update the state with both results
          setData({
            maxRedeemable: maxRedeemable,
            apr: apr,
            totalHYPEStaked: totalHYPEStaked && formatUnits(totalHYPEStaked, 18),
            uniqueMintsFrom: uniqueMintsFrom,
            rateStHYPEToHYPE: rateStHYPEToHYPE,
          })

          setLoading(false)
        }
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    }

    if (!chainId || !library || !account) return
    if (loading) {
      fetchData()
    }
  }, [account, chainId, library, loading])
  const setRefresh = () => {
    setLoading(true)
  }

  const maxRedeemableFormatted = data?.maxRedeemable && formatUnits(data?.maxRedeemable, 18)

  const randomKey = Math.random() * 99999
  return (
    <>
      <IntroCard variant={2} key="stHype">
        <h1>Stake HYPE, Earn stHYPE</h1>
        <p>Powered by Thunderhead</p>
        <Links>
          <a href="https://thunderhead.xyz/" target="_blank" rel="noreferrer" aria-label="Thunderhead Website">
            {LINKS['landingPage'].logo}
          </a>
          <a href="https://x.com/thunderheadxyz" target="_blank" rel="noreferrer" aria-label="Thunderhead Twitter">
            {LINKS['twitter'].logo}
          </a>
          <a href="https://discord.gg/thunderhead" target="_blank" rel="noreferrer" aria-label="Thunderhead Discord">
            {LINKS['discord'].logo}
          </a>
        </Links>
      </IntroCard>
      <AutoColumn gap="lg" justify="center">
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <TitleRow style={{ marginTop: '1rem' }} padding={'0'}>
            <TYPE.mediumHeader>{'Staking Pool Overview'}</TYPE.mediumHeader>
          </TitleRow>
          {!account ? (
            <ConnectWalletContainer>
              <div>
                <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-tax"
                    style={{ marginBottom: '.5rem' }}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M8.487 21h7.026a4 4 0 0 0 3.808 -5.224l-1.706 -5.306a5 5 0 0 0 -4.76 -3.47h-1.71a5 5 0 0 0 -4.76 3.47l-1.706 5.306a4 4 0 0 0 3.808 5.224" />
                    <path d="M15 3q -1 4 -3 4t -3 -4z" />
                    <path d="M14 11h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3h-2.5" />
                    <path d="M12 10v1" />
                    <path d="M12 17v1" />
                  </svg>

                  <div>{'Connect your wallet to stake HYPE and earn stHYPE.'}</div>
                </TYPE.mediumHeader>
                <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                  Connect a wallet
                </ButtonPrimary>
              </div>
            </ConnectWalletContainer>
          ) : (
            <GridContainer>
              <SubContainer bg1={false}>
                <>
                  <SubContainerHeader>
                    <div style={{ display: 'flex', fontSize: '16px', marginBottom: '12px' }}>
                      <CurrencyLogo currency={stHYPEToken as Currency} size={'42px'} />
                      <h2 style={{ fontSize: '16px', marginLeft: '12px' }}>Earn stHYPE</h2>
                    </div>

                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 800 }}>Annual Protocol Reward</span>
                      <span style={{ fontSize: '14px', fontWeight: 800 }}>Total HYPE Staked</span>
                    </EntriesContainer>
                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                        {loading || !data?.apr ? (
                          <SkeletonText width="40%" height="100%" />
                        ) : (
                          `${roundToDecimalPlace(data?.apr, '3')}%`
                        )}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                        {loading || !data?.totalHYPEStaked ? (
                          <SkeletonText width="30%" height="100%" />
                        ) : (
                          `${roundToDecimalPlace(data?.totalHYPEStaked, '3')} HYPE`
                        )}
                      </span>
                    </EntriesContainer>

                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 800 }}>Unique Stakers</span>
                      <span style={{ fontSize: '14px', fontWeight: 800 }}>Max Redeemable</span>
                    </EntriesContainer>
                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                        {!data?.uniqueMintsFrom || loading ? (
                          <SkeletonText width="75%" height="100%" />
                        ) : (
                          `${data?.uniqueMintsFrom}`
                        )}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                        {!data?.maxRedeemable || loading ? (
                          <SkeletonText width="55%" height="100%" />
                        ) : (
                          `${roundToDecimalPlace(maxRedeemableFormatted, '10')} HYPE`
                        )}
                      </span>
                    </EntriesContainer>
                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 800 }}>Exchange Rate</span>
                    </EntriesContainer>
                    <EntriesContainer>
                      <span style={{ fontSize: '14px', fontWeight: 400, height: '16px' }}>
                        {!data?.apr || loading ? <SkeletonText width="30%" height="100%" /> : `1 stHYPE = 1 HYPE`}
                      </span>
                    </EntriesContainer>
                    <p style={{ marginBottom: '0' }}>
                      <u>Disclaimer</u>: The staking services provided on this page are powered by Thunderhead and are
                      not directly developed or maintained by HyperSwap Exchange. While we aim to collaborate with
                      reputable partners, please note that HyperSwap Exchange does not control or manage the underlying
                      smart contracts associated with these staking operations.
                    </p>
                  </SubContainerHeader>

                  <SubContainerBody>
                    <div style={{ marginTop: '16px' }}>
                      <EntriesContainer>
                        <span style={{ fontWeight: 800, fontSize: '16px' }}>Staked</span>
                      </EntriesContainer>
                      <EntriesContainer>
                        {loading ? (
                          <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38', height: '20px' }}>
                            <SkeletonText width="55%" height="100%" />
                          </span>
                        ) : (
                          <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38' }}>
                            {roundToDecimalPlace(`${tokenBalance?.toExact()}`, '5')} stHYPE
                          </span>
                        )}
                      </EntriesContainer>
                    </div>
                  </SubContainerBody>
                </>
              </SubContainer>
              <div>
                <SubContainer bg1={false}>
                  <SubContainerHeader>
                    <Tabs>
                      <StyledTabButton isActive={tab === 0} onClick={() => setTab(0)}>
                        Stake
                      </StyledTabButton>
                      <StyledTabButton isActive={tab === 1} onClick={() => setTab(1)}>
                        Unstake
                      </StyledTabButton>
                    </Tabs>

                    {tab === 0 ? (
                      <>
                        <p>
                          Earn Rewards Protocol rewards earned from validator nodes will accrue automatically. stHYPE
                          uses a rebase mechanism to allow balances to increase in line with protocol rewards. You are
                          now helping secure the Hyperliquid ecosystem and getting the rewards for doing so.
                        </p>
                        <p style={{ color: 'white', marginBottom: '0', fontSize: '14px' }}>
                          Please enter the amount of HYPE Tokens you wish to stake.
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          <u>Note</u>: If there isn’t enough HYPE liquidity to instantly unstake, you’ll need to wait
                          until additional HYPE tokens become available from validators.
                        </p>
                        <p style={{ color: 'white', marginBottom: '0', fontSize: '14px' }}>
                          Please enter the amount of stHYPE Tokens you wish to unstake.
                        </p>
                      </>
                    )}
                  </SubContainerHeader>
                  {tab === 0 ? (
                    <Stake setRefresh={setRefresh} key={randomKey} />
                  ) : (
                    <Unstake setRefresh={setRefresh} maxRedeemable={data?.maxRedeemable ?? '0'} key={randomKey} />
                  )}
                </SubContainer>
              </div>
            </GridContainer>
          )}
        </AutoColumn>
      </AutoColumn>
    </>
  )
}
export default EarnSTHYPE
